import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CircularProgress, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { resources } from '../../../core/services';
import { OnboardingWizardShell } from '../shared/OnboardingWizardShell';
import { isDescriptionValid } from '../shared/DescriptionStep';
import { useOnboardingDraft } from '../shared/useOnboardingDraft';
import { useCreateAndStartProject } from '../shared/useCreateAndStartProject';
import { suggestProjectName } from '../shared/projectName';
import { getProjectTypeProfile } from '../projectTypeProfiles';
import { INTRO_STEP_KEY, buildValidationSteps, buildValidationStages } from '../../validation/validationStages';
import { validationCopyFor } from '../../validation/validationCopy';
import { CompanySourcesStep, isCompanySourcesStepValid } from './CompanySourcesStep';
import { businessValidationCopy as copy } from './copy';

/**
 * One screen: the company's public sources and the investor's focus points,
 * then "Start validation" creates the project (named from the website) and
 * starts the source analysis, which writes the company brief and generates
 * the first stage. The progress line already shows the whole journey — the
 * stages and the summary continue on the project's validation page.
 */
export function BusinessValidationWizard() {
  const profile = getProjectTypeProfile('BUSINESS_VALIDATION');
  const { draft, updateDraft, clearDraft } = useOnboardingDraft(profile.type);
  const { submit, status, error, clearError } = useCreateAndStartProject(profile, { onCreated: clearDraft });

  const docTypesQuery = useQuery({
    queryKey: ['documentTypes'],
    queryFn: () => resources.documentTypes.list({ pageSize: 500, sort: 'order' }),
  });
  const steps = useMemo(
    () =>
      buildValidationSteps(buildValidationStages(docTypesQuery.data?.data ?? [], [], profile.validationCategory), {
        intro: { label: validationCopyFor(profile.type).intro.stepLabel, state: 'active', clickable: false },
        generatingType: null,
      }),
    [docTypesQuery.data, profile],
  );

  const busy = status !== 'idle';
  const nextLabel = status === 'creating' ? 'Creating project…' : status === 'starting' ? 'Starting validation…' : copy.start;
  const canProceed =
    isCompanySourcesStepValid(draft, profile) && isDescriptionValid(draft.description, profile.inputs.description === 'required');

  return (
    <OnboardingWizardShell
      title={copy.title}
      subtitle={copy.subtitle}
      steps={steps}
      activeStep={steps.findIndex((s) => s.key === INTRO_STEP_KEY)}
      canProceed={canProceed}
      onBack={() => undefined}
      onNext={() => void submit({ ...draft, name: suggestProjectName(draft) })}
      nextLabel={nextLabel}
      nextIcon={busy ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
      busy={busy}
      error={error}
      onClearError={clearError}
    >
      <CompanySourcesStep draft={draft} onChange={updateDraft} profile={profile} />
      <Typography variant="caption" color="text.secondary">
        {copy.startHint}
      </Typography>
    </OnboardingWizardShell>
  );
}
